<!-- 工单详情组件 -->
<template>
  <el-dialog
    title="工单详情"
    :visible.sync="dialogVisible"
    width="900px"
    top="5vh"
    :append-to-body="true"
    :close-on-click-modal="false"
    class="work-order-detail-dialog"
    @close="handleClose"
  >
    <div class="detail-container" v-loading="loading">
      <!-- 工单信息 -->
      <div class="detail-section">
        <div class="section-title">工单信息</div>
        <el-descriptions :column="2" size="small" border>
          <el-descriptions-item label="工单编号">
            {{ detailData.workOrderId || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="工单状态">
            <el-tag :type="statusType(detailData.orderStatus)" size="small">
              {{ detailData.orderStatusStr }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="优先级">
            <el-tag :type="priorityType(detailData.priority)" size="small">
              {{ priorityText(detailData.priority) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="报修人">
            {{ detailData.reporterName || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="手机号">
            {{ detailData.reporterPhone || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="设备名称" :span="2">
            {{ detailData.deviceName || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="故障地点" :span="2">
            {{ detailData.faultLocation || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="故障描述" :span="2">
            {{ detailData.faultDescription || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="负责工程师" :span="2">
            {{ detailData.responsibleEngineerName || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="创建人" :span="2">
            {{ detailData.createName || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="创建时间" :span="2">
            {{ detailData.createTime || '-' }}
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 故障图片 -->
      <div class="detail-section" v-if="parseImages(detailData.imagePath).length > 0">
        <div class="section-title">故障图片</div>
        <div class="image-list">
          <div
            v-for="(img, idx) in parseImages(detailData.imagePath)"
            :key="idx"
            class="image-item"
          >
            <el-image
              :src="img"
              :preview-src-list="parseImages(detailData.imagePath)"
              fit="cover"
            >
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
            <div class="image-name">故障图片{{ idx + 1 }}.jpg</div>
            <a :href="img" download class="image-download">
              <i class="el-icon-download"></i> 下载
            </a>
          </div>
        </div>
      </div>

      <!-- 流程节点 -->
      <div class="detail-section">
        <div class="section-title">流程节点</div>
        <el-timeline>
          <el-timeline-item
            v-for="(item, index) in detailData.workOrderExtList || []"
            :key="index"
            :timestamp="item.processTime || item.createTime"
            :type="getTimelineType(item.orderStatus)"
            :icon="getTimelineIcon(item.orderStatus)"
            placement="top"
          >
            <el-card>
              <div class="timeline-header">
                <el-tag :type="statusType(item.orderStatus)" size="small">
                  {{ statusText(item.orderStatus) }}
                </el-tag>
                <span class="timeline-time">{{ item.processTime || item.createTime }}</span>
              </div>

              <div class="timeline-content">
                <div class="timeline-row" v-if="item.responsibleEngineerName">
                  <span class="label">负责工程师：</span>
                  <span class="value">{{ item.responsibleEngineerName }}</span>
                </div>
                <div class="timeline-row" v-if="item.teamName">
                  <span class="label">班次名称：</span>
                  <span class="value">{{ item.teamName }}</span>
                </div>
                <div class="timeline-row" v-if="item.handlingMethod">
                  <span class="label">处理方式：</span>
                  <span class="value">{{ item.handlingMethod }}</span>
                </div>
                <div class="timeline-row" v-if="item.imageDesc">
                  <span class="label">图片描述：</span>
                  <span class="value">{{ item.imageDesc }}</span>
                </div>

                <!-- 处理图片 -->
                <div class="timeline-images" v-if="parseImages(item.imagePath).length > 0">
                  <div class="label">图片：</div>
                  <div class="image-list-small">
                    <el-image
                      v-for="(img, idx) in parseImages(item.imagePath)"
                      :key="idx"
                      :src="img"
                      :preview-src-list="parseImages(item.imagePath)"
                      fit="cover"
                      class="timeline-image"
                    >
                      <div slot="error" class="image-slot">
                        <i class="el-icon-picture-outline"></i>
                      </div>
                    </el-image>
                  </div>
                </div>

                <div class="timeline-row" v-if="item.createName">
                  <span class="label">操作人：</span>
                  <span class="value">{{ item.createName }}</span>
                </div>
                <div class="timeline-row" v-if="item.remark">
                  <span class="label">备注：</span>
                  <span class="value">{{ item.remark }}</span>
                </div>
              </div>
            </el-card>
          </el-timeline-item>
        </el-timeline>
      </div>

      <!-- 处理详情 -->
      <div class="detail-section">
        <div class="section-title">处理详情</div>
        <el-descriptions :column="1" size="small" border>
          <el-descriptions-item label="处理方式">
            {{ detailData.handlingMethod}}
          </el-descriptions-item>
          <el-descriptions-item label="处理备注">
            {{ detailData.remark || '-' }}
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 上传附件 -->
      <div class="detail-section" v-if="detailData.workOrderExtList && detailData.workOrderExtList.length > 0">
        <div class="section-title">上传附件</div>
        <div class="attachment-list">
          <div
            v-for="(ext, index) in detailData.workOrderExtList"
            :key="index"
            class="attachment-group"
          >
            <div
              v-for="(img, idx) in parseImages(ext.imagePath)"
              :key="`${index}-${idx}`"
              class="attachment-item"
            >
              <i class="el-icon-document"></i>
              <span class="attachment-name">{{ getFileName(img, index, idx) }}</span>
              <a :href="img" download class="attachment-download">
                <i class="el-icon-download"></i> 下载
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭详情</el-button>
      <el-button type="primary" @click="handleProcess" v-if="showProcessButton">
        {{ processButtonText }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'WorkOrderDetail',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    workOrderId: {
      type: [String, Number],
      default: null
    },
    // 获取详情的API方法
    getDetailApi: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      detailData: {}
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    },
    // 是否显示处理按钮
    showProcessButton() {
      const status = this.detailData.orderStatus;
      return [0, 1, 2].includes(status);
    },
    // 处理按钮文字
    processButtonText() {
      const status = this.detailData.orderStatus;
      if (status === 0) return '开始处理';
      if (status === 1) return '完成处理';
      if (status === 2) return '验收通过';
      return '处理';
    }
  },
  watch: {
    visible(val) {
      if (val && this.workOrderId) {
        this.loadDetail();
      }
    },
    workOrderId(val) {
      if (val && this.visible) {
        this.loadDetail();
      }
    }
  },
  methods: {
    // 加载详情数据
    loadDetail() {
      if (!this.workOrderId) {
        this.$message.warning('工单ID不能为空');
        return;
      }

      this.loading = true;
      this.getDetailApi(this.workOrderId).then(res => {
        const data = res.data.data || res.data;
        console.log('工单详情数据:', data);
        this.detailData = data;
        this.loading = false;
      }).catch(error => {
        console.error('获取详情失败:', error);
        this.$message.error('获取详情失败');
        this.loading = false;
        this.handleClose();
      });
    },

    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false;
      this.detailData = {};
    },

    // 处理按钮点击
    handleProcess() {
      const status = this.detailData.orderStatus;
      const actionMap = {
        0: 'start',    // 开始处理
        1: 'finish',   // 完成处理
        2: 'accept'    // 验收通过
      };

      const action = actionMap[status];
      if (action) {
        this.$emit('process', {
          action,
          data: this.detailData
        });
        this.handleClose();
      } else {
        this.$message.info('当前状态无法进行处理');
      }
    },

    // 工具方法
    priorityText(v) {
      return { 1: "紧急",2: "一级", 3: "二级", 4: "三级" }[v] || "-";
    },
    priorityType(v) {
      return { 1: "danger", 2: "warning",3: "warning", 4: "info" }[v] || "info";
    },
    statusText(v) {
      const statusMap = {
        0: "已发起",
        1: "处理中",
        2: "待验收",
        3: "已完成",
        4: "验收未通过",
        5: "已取消"
      };
      return statusMap[v] || "-";
    },
    statusType(v) {
      return {
        0: "info",
        1: "warning",
        2: "",
        3: "success",
        4: "danger",
        5: "info",
      }[v] || "";
    },
    parseImages(pathStr) {
      if (!pathStr) return [];
      return pathStr.split(",").map((s) => s.trim()).filter(s => s);
    },
    getTimelineType(status) {
      return {
        0: 'primary',
        1: 'warning',
        2: 'info',
        3: 'success',
        4: 'danger',
        5: 'info',
      }[status] || 'info';
    },
    getTimelineIcon(status) {
      return {
        0: 'el-icon-edit',
        1: 'el-icon-loading',
        2: 'el-icon-question',
        3: 'el-icon-circle-check',
        4: 'el-icon-circle-close',
        5: 'el-icon-remove',
      }[status] || 'el-icon-info';
    },
    getFileName(url, nodeIndex, fileIndex) {
      const fileName = url.split('/').pop();
      if (fileName && fileName.includes('.')) {
        return fileName;
      }
      return `图片${fileIndex + 1}.jpg`;
    }
  }
};
</script>

<style lang="scss" scoped>
.work-order-detail-dialog {
  ::v-deep .el-dialog__body {
    max-height: 75vh;
    overflow-y: auto;
    padding: 10px 20px;
  }
}

.detail-container {
  .detail-section {
    margin-bottom: 24px;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      padding: 10px 0;
      margin-bottom: 16px;
      //border-bottom: 2px solid #409EFF;
    }
  }

  // 图片列表样式
  .image-list {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;

    .image-item {
      position: relative;
      width: 120px;

      ::v-deep .el-image {
        width: 120px;
        height: 120px;
        border-radius: 4px;
        border: 1px solid #DCDFE6;
        cursor: pointer;

        &:hover {
          border-color: #409EFF;
        }
      }

      .image-name {
        font-size: 12px;
        color: #606266;
        text-align: center;
        margin-top: 8px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .image-download {
        display: block;
        text-align: center;
        font-size: 12px;
        color: #409EFF;
        text-decoration: none;
        margin-top: 4px;

        &:hover {
          color: #66B1FF;
        }

        i {
          margin-right: 4px;
        }
      }
    }
  }

  // 时间线样式
  ::v-deep .el-timeline {
    padding-left: 10px;

    .el-timeline-item__timestamp {
      color: #909399;
      font-size: 13px;
    }

    .el-card {
      margin-bottom: 0;

      .el-card__body {
        padding: 16px;
      }
    }
  }

  .timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #EBEEF5;

    .timeline-time {
      font-size: 13px;
      color: #909399;
    }
  }

  .timeline-content {
    .timeline-row {
      display: flex;
      margin-bottom: 8px;
      font-size: 14px;
      line-height: 1.6;

      .label {
        color: #606266;
        font-weight: 500;
        min-width: 100px;
        flex-shrink: 0;
      }

      .value {
        color: #303133;
        flex: 1;
      }
    }

    .timeline-images {
      margin-top: 12px;

      .label {
        color: #606266;
        font-weight: 500;
        margin-bottom: 8px;
        display: block;
      }

      .image-list-small {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        .timeline-image {
          width: 80px;
          height: 80px;
          border-radius: 4px;
          border: 1px solid #DCDFE6;
          cursor: pointer;

          &:hover {
            border-color: #409EFF;
          }
        }
      }
    }
  }

  // 附件列表样式
  .attachment-list {
    .attachment-group {
      margin-bottom: 12px;
    }

    .attachment-item {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      background-color: #F5F7FA;
      border-radius: 4px;
      margin-bottom: 8px;
      transition: all 0.3s;

      &:hover {
        background-color: #ECF5FF;
      }

      i.el-icon-document {
        font-size: 24px;
        color: #409EFF;
        margin-right: 12px;
      }

      .attachment-name {
        flex: 1;
        font-size: 14px;
        color: #303133;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .attachment-download {
        font-size: 13px;
        color: #409EFF;
        text-decoration: none;
        margin-left: 12px;

        &:hover {
          color: #66B1FF;
        }

        i {
          margin-right: 4px;
        }
      }
    }
  }

  // 图片加载失败占位符
  .image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: #F5F7FA;
    color: #C0C4CC;
    font-size: 30px;
  }
}

// 对话框底部按钮
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  .el-button{
    padding: 0px 20px;
  }
}
</style>

